/**
 * "Account Information" (business contacts) validation.
 *
 * Mirrors app/Traits/CustomerContactValidationTraits.php and the
 * accounting_contacts loop in Store/UpdateCustomerRequest on the BE, so the grid
 * blocks exactly what the API would reject and says the same thing about it.
 * Keeping the two in step is the point of this file — CreateCustomer and
 * ViewCustomer used to carry their own partial copies of the rules, which is how
 * a row could pass the screen and still come back rejected.
 *
 * Messages are declarative rather than exclamatory (ESCM-13070): each states the
 * rule and points at the record to check.
 */

const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SECTION = 'Account Information';

/**
 * What an operator types into CONTACT NO. when the contact has no number to
 * record. Not every contact has one to begin with, so "N/A" has to be accepted
 * — its slash would otherwise read as a separator between two numbers.
 *
 * A placeholder carries no digits at all: "0917 N/A" is a number with a stray
 * note rather than a "none on file" marker, and still gets checked normally.
 */
const NO_CONTACT_NO_PLACEHOLDERS = ['na', 'none', 'nil', 'wala'];

export const isNoContactNoPlaceholder = (value) => {
  const contactNo = String(value ?? '').trim();
  if (contactNo === '' || /\d/.test(contactNo)) return false;
  return NO_CONTACT_NO_PLACEHOLDERS.includes(
    contactNo.replace(/[^A-Za-z]/g, '').toLowerCase(),
  );
};

/**
 * One number per box, since the box maps to one column. A second number arrives
 * either behind a separator (spaces excluded — they are legitimate inside a
 * single number) or run straight on to the first, which only the digit count
 * catches. 15 digits is E.164's maximum for a single international number.
 */
export const isSingleContactNo = (value) => {
  const contactNo = String(value ?? '').trim();
  if (contactNo === '' || isNoContactNoPlaceholder(contactNo)) return true;
  if (/[,;/&]/.test(contactNo)) return false;
  return contactNo.replace(/\D/g, '').length <= 15;
};

/** Whitespace IS a separator here, unlike in a number: an address has none. */
export const isSingleEmailAddress = (value) =>
  !/[,;/\s]/.test(String(value ?? '').trim());

export const isValidEmail = (value) =>
  EMAIL_RE.test(String(value ?? '').trim());

/**
 * An email address is only mandatory for the contacts documents are sent to.
 * Any other contact may have none on file.
 */
export const isDocumentRecipient = (contact) =>
  !!contact.set_as_ecr_recipient || !!contact.set_as_soa_recipient;

/** The per-row complaint about CONTACT NO., or '' when the box is acceptable. */
export const contactNoError = (value) =>
  isSingleContactNo(value)
    ? ''
    : 'Only one (1) contact number is allowed per person.';

/** The per-row complaint about EMAIL ADDRESS, or '' when acceptable. */
export const emailError = (contact) => {
  const email = String(contact.email ?? '').trim();

  if (email === '') {
    return isDocumentRecipient(contact)
      ? 'An email address is required for an E-CR or SOA Recipient.'
      : '';
  }

  if (!isSingleEmailAddress(email)) {
    return 'Only one (1) email address is allowed per person.';
  }

  return isValidEmail(email) ? '' : 'Please enter a valid email address.';
};

/**
 * Every message the BE would raise for this list, in row order. An empty array
 * means the list will save.
 *
 * Accepts rows in either shape: the grid holds the name under `name`, the API
 * payload under `contact_person`.
 */
export const validateAccountingContacts = (contacts = []) => {
  const messages = [];

  if (!Array.isArray(contacts) || contacts.length === 0) {
    return [`At least one contact is required in ${SECTION}.`];
  }

  // Disabled: contacts may share a name, contact number or email address
  // (ESCM-13655 Business Contacts - Account Information Adjustment), matching
  // the BE where validateNoDuplicateContacts() is no longer called.
  // const seenContactNo = {};
  // const seenEmail = {};

  contacts.forEach((contact, index) => {
    const row = index + 1;
    const name = String(contact.name ?? contact.contact_person ?? '').trim();
    const contactNo = String(contact.contact_no ?? '').trim();
    const email = String(contact.email ?? '').trim();

    if (name === '') {
      messages.push(`Name of Contact is required in ${SECTION} #${row}.`);
    }

    if (!isSingleContactNo(contactNo)) {
      messages.push(
        `Only one (1) contact number is allowed per person. Please check the records. (${SECTION} #${row})`,
      );
    }

    // ESCM-13655: duplicate contact numbers are allowed.
    // // Compared on digits only, so formatting cannot hide a duplicate and an
    // // "N/A" placeholder normalises to empty — any number of contacts may be
    // // marked as having no number.
    // const digits = contactNo.replace(/\D/g, '');
    // if (digits !== '') {
    //   if (seenContactNo[digits]) {
    //     messages.push(
    //       `The contact number in ${SECTION} #${row} is already used in #${seenContactNo[digits]}. Each contact must have its own contact number. Please check the records.`,
    //     );
    //   } else {
    //     seenContactNo[digits] = row;
    //   }
    // }

    if (email === '') {
      if (isDocumentRecipient(contact)) {
        messages.push(
          `An email address is required in ${SECTION} #${row} because the contact is tagged as an E-CR or SOA Recipient.`,
        );
      }
    } else if (!isSingleEmailAddress(email)) {
      messages.push(
        `Only one (1) email address is allowed per person. Please check the records. (${SECTION} #${row})`,
      );
    } else if (!isValidEmail(email)) {
      messages.push(
        `The email address in ${SECTION} #${row} is not valid. Please check the records.`,
      );
    }

    // ESCM-13655: duplicate email addresses are allowed.
    // const emailKey = email.toLowerCase();
    // if (emailKey !== '') {
    //   if (seenEmail[emailKey]) {
    //     messages.push(
    //       `The email address in ${SECTION} #${row} is already used in #${seenEmail[emailKey]}. Each contact must have its own email address. Please check the records.`,
    //     );
    //   } else {
    //     seenEmail[emailKey] = row;
    //   }
    // }
  });

  if (!contacts.some((contact) => contact.set_as_ecr_recipient)) {
    messages.push(`Please tag at least one E-CR Recipient in ${SECTION}.`);
  }

  if (!contacts.some((contact) => contact.set_as_soa_recipient)) {
    messages.push(`Please tag at least one SOA Recipient in ${SECTION}.`);
  }

  return messages;
};
